import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Supabase direct queries (student, activities, documents)
// ---------------------------------------------------------------------------

export async function getStudent(userId) {
  const { data, error } = await supabase
    .from("students")
    .select("*")
    .eq("id", userId)
    .single();
  if (error) {
    console.error("[getStudent]", error);
    throw error;
  }
  return data;
}

export async function getActivities(userId) {
  const [templatesRes, progressRes] = await Promise.all([
    supabase
      .from("activity_templates")
      .select("*")
      .eq("is_active", true)
      .order("block_order")
      .order("sort_order"),
    supabase
      .from("student_progress")
      .select("*")
      .eq("student_id", userId),
  ]);

  if (templatesRes.error) {
    console.error("[getActivities] templates", templatesRes.error);
    throw templatesRes.error;
  }

  const progressMap = new Map(
    (progressRes.data || []).map((p) => [p.template_id, p])
  );

  return (templatesRes.data || []).map((t) => {
    const progress = progressMap.get(t.id);
    return {
      ...t,
      template_id: t.id,
      completed: progress?.completed || false,
      completed_at: progress?.completed_at || null,
      response: progress?.response || null,
      notes: progress?.notes || null,
      updated_at: progress?.updated_at || null,
      progress_id: progress?.id || null,
    };
  });
}

export async function updateActivity(studentId, templateId, updates) {
  const { data, error } = await supabase
    .from("student_progress")
    .upsert(
      {
        student_id: studentId,
        template_id: templateId,
        ...updates,
      },
      { onConflict: "student_id,template_id" }
    )
    .select()
    .single();

  if (error) {
    console.error("[updateActivity]", error);
    throw error;
  }
  return data;
}

export async function markTourSeen(userId) {
  const { error } = await supabase
    .from("students")
    .update({ has_seen_tour: true })
    .eq("id", userId);
  if (error) {
    console.error("[markTourSeen]", error);
  }
}

export async function markPasswordSet(userId) {
  const { error } = await supabase
    .from("students")
    .update({ has_set_password: true })
    .eq("id", userId);
  if (error) {
    console.error("[markPasswordSet]", error);
  }
}

export async function getDocuments(userId) {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("student_id", userId)
    .order("created_at");
  if (error) {
    console.error("[getDocuments]", error);
    throw error;
  }
  return data;
}

// ---------------------------------------------------------------------------
// Skool invite (calls Edge Function → Zapier webhook)
// ---------------------------------------------------------------------------

export async function requestSkoolInvite() {
  const { data, error } = await supabase.functions.invoke("skool-invite", {
    body: {},
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// ---------------------------------------------------------------------------
// Airtable proxy queries (payments, sessions) — with Supabase fallback
// ---------------------------------------------------------------------------

async function queryAirtable(table) {
  const { data, error } = await supabase.functions.invoke("airtable-proxy", {
    body: { table },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);

  return data?.data ?? [];
}

async function supabaseFallback(tableName, userId, orderBy = "created_at") {
  const { data, error } = await supabase
    .from(tableName)
    .select("*")
    .eq("student_id", userId)
    .order(orderBy);
  if (error) throw error;
  return data;
}

export async function getPayments(userId) {
  try {
    return await queryAirtable("pagos");
  } catch (err) {
    console.warn("[pagos] Airtable no disponible, usando Supabase:", err.message);
    return supabaseFallback("payments", userId, "date");
  }
}

export async function getSessions(userId) {
  try {
    return await queryAirtable("sesiones");
  } catch (err) {
    console.warn("[sesiones] Airtable no disponible, usando Supabase:", err.message);
    return supabaseFallback("sessions", userId, "date");
  }
}
